import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DateRange2 from "../../Component/DateRange/DateRange2";
import { post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import { Toast } from "../../Component/Toast";

// Convert "DD-MM-YYYY" → "YYYY-MM-DD"
const convertDateToISO = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm}-${dd}`;
};

const AnnounceResultEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { editData } = location.state || {};

    const [loading, setLoading] = useState(false);
    const [popup, setPopup] = useState(false);
    const [drawDate, setDrawDate] = useState("");
    const [lotDraw, setLotDraw] = useState("");
    const [timeDraw, setTimeDraw] = useState(null);
    const [selectedTimeId, setSelectedTimeId] = useState(null);
    const [resultNumber, setResultNumber] = useState(["", "", "", ""]);

    const [errors, setErrors] = useState({});
    const [showResult, setShowResult] = useState(false);

    const [backupData, setBackupData] = useState(null);

    const [lotOptions, setLotOptions] = useState([]);
    const [timeOptions, setTimeOptions] = useState([]);

    // ----------------------------------------------------------------
    // INITIAL LOAD FOR EDIT MODE
    // ----------------------------------------------------------------
    useEffect(() => {
        if (!editData) return;

        // DATE
        const isoDate = convertDateToISO(editData.date);
        setDrawDate(isoDate);

        // LOT NAME
        const lotObj = { value: editData.lot_name, label: editData.lot_name };
        setLotOptions([lotObj]);
        setLotDraw(lotObj);

        // TIME
        const timeObj = {
            value: editData.time,
            label: editData.time,
            id: editData.id
        };
        setTimeOptions([timeObj]);
        setTimeDraw(timeObj);
        setSelectedTimeId(editData.id);

        // RESULT NUMBER
        if (editData.winning_number) {
            const num = editData.winning_number.toString().split("");
            while (num.length < 4) num.push("");
            setResultNumber(num);
        }
    }, [editData]);

    // ----------------------------------------------------------------
    // INPUT HANDLER
    // ----------------------------------------------------------------
    const handleChange = (e) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 4);
        const arr = value.split("");
        while (arr.length < 4) arr.push("");
        setResultNumber(arr);
    };

    // ----------------------------------------------------------------
    // CHECK RESULT
    // ----------------------------------------------------------------
    const handleCheckResult = () => {
        let newErrors = {};

        if (!lotDraw) newErrors.lot = "Please select Lot Name";
        if (!timeDraw) newErrors.time = "Please select Time";
        if (resultNumber.join("").length !== 4)
            newErrors.resultNumber = "Please enter 4 digits";

        setErrors(newErrors);

        if (Object.keys(newErrors).length !== 0) {
            setShowResult(false);
            return;
        }

        // Save values before clearing
        setBackupData({
            drawDate,
            lotDraw,
            timeDraw,
            resultNumber,
        });

        // Show preview
        setShowResult(true);

        // Clear all inputs exactly as you want
        setDrawDate("");
        setLotDraw("");
        setTimeDraw(null);
        setResultNumber(["", "", "", ""]);
    };

    // ----------------------------------------------------------------
    // EDIT BUTTON → RESTORE DATA
    // ----------------------------------------------------------------
    const handleEdit = () => {
        if (!backupData) return;

        setDrawDate(backupData.drawDate);
        setLotDraw(backupData.lotDraw);
        setTimeDraw(backupData.timeDraw);
        setResultNumber(backupData.resultNumber);

        setShowResult(false);
        setErrors({});
    };

    // ----------------------------------------------------------------
    // ANNOUNCE RESULT API
    // ----------------------------------------------------------------
    const handleAnnounceResult = async () => {
        setLoading(true);

        const postData = {
            id: selectedTimeId,
            winning_number: backupData?.resultNumber.join("")
        };

        const res = await post(URLS.RESULTANNOUNCE, postData);

        if (res.status === "success") {
            Toast(res.message || "Result Announced Successfully");
            setPopup(false);
            navigate(-1);
        }
        setLoading(false);
    };

    // ----------------------------------------------------------------
    // SELECT STYLES
    // ----------------------------------------------------------------
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: 8,
            border: "1px solid #00474366",
            border: `1px solid ${state.isFocused || state.hasValue ? "#004743" : "#00474366"}`,
            boxShadow: state.isFocused
                ? "transparent"
                : "0 0 0 1px #dffffd66",
            "&:hover": {
                border: "1px solid #00474366",
            },
            backgroundColor: "#F0EDE6",
            minHeight: 51,
            padding: "3px 10px",
            fontFamily: state.isSelected ? "f3" : "f2",
            color: "#000",
        }),

        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            backgroundColor: "#F0EDE6",
            borderRadius: 8,
        }),

        menuList: (provided) => ({
            ...provided,
            backgroundColor: "#F0EDE6",
            borderRadius: 8,
        }),

        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? "transparent"
                : state.isFocused
                    ? "transparent"
                    : "transparent",
            color: state.isSelected
                ? "#00211fff"
                : "#004743",
            cursor: "pointer",
            ":active": {
                backgroundColor: "#00474333",
            },
        }),

        singleValue: (provided) => ({
            ...provided,
            color: "#004743",
        }),

        indicatorSeparator: () => ({
            display: "none",
        }),

        dropdownIndicator: (provided) => ({
            ...provided,
            color: "#004743",
            "&:hover": {
                color: "#004743",
            },
        }),
    };

    return (
        <div className="dashRightView ">
            <div style={{ paddingTop: 0 }} className="container_overall">
                {/* DATE */}
                <div className="announcement_date mb-3 col-md-5">
                    <DateRange2 selectedDate={drawDate} setSelectedDate={setDrawDate} />
                </div>

                {/* LOT NAME */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Lot Name</label>
                    <Select
                        className="location-search"
                        value={lotDraw}
                        onChange={setLotDraw}
                        options={lotOptions}
                        placeholder="Select Lot"
                        styles={customStyles}
                        isSearchable={false}
                        noOptionsMessage={() => ""}
                    />
                    {errors.lot && <p className="err">{errors.lot}</p>}
                </div>

                {/* TIME */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Time</label>
                    <Select
                        className="location-search"
                        value={timeDraw}
                        onChange={(selected) => {
                            setTimeDraw(selected);
                            setSelectedTimeId(selected?.id);
                        }}
                        options={timeOptions}
                        placeholder="Select Time"
                        styles={customStyles}
                        isSearchable={false}
                        noOptionsMessage={() => ""}
                    />
                    {errors.time && <p className="err">{errors.time}</p>}
                </div>

                {/* RESULT NUMBER */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Result Number</label>
                    <input
                        className="input-box placholdercolorchanges2"
                        type='number'
                        value={resultNumber.join("")}
                        onChange={handleChange}
                        maxLength={4}
                        placeholder="Enter Result Number"
                    />
                    {errors.resultNumber && <p className="err">{errors.resultNumber}</p>}
                </div>

                <div className="my-3 col-md-5">
                    <button className="add-button f3 w-100 fs-xs-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19 white" onClick={handleCheckResult}>
                        Check Result
                    </button>
                </div>

                {/* PREVIEW SECTION */}
                {showResult && (
                    <div className="my-2 col-md-5">
                        <div className="d-flex ac-jb">
                            <label className="form-label f3 primary">Check Result</label>
                            <button className="edit1-btn" onClick={handleEdit}>Edit</button>
                        </div>

                        <div className="check_result my-2">

                            <div className="d-flex ac-jb my-2">
                                <p className="f3">Date</p>
                                <p className="f2 gray">
                                    {new Date(backupData.drawDate)
                                        .toLocaleDateString("en-GB")
                                        .replace(/\//g, "-")}
                                </p>
                            </div>

                            <div className="d-flex ac-jb my-2">
                                <p className="f3">Lot Name</p>
                                <p className="f2 gray">{backupData.lotDraw.label}</p>
                            </div>

                            <div className="d-flex ac-jb my-2">
                                <p className="f3">Time</p>
                                <p className="f2 gray">{backupData.timeDraw.label}</p>
                            </div>

                            <div className="d-flex ac-jb">
                                <p className="f3">Result Number</p>
                                <div className="d-flex gap-2">
                                    {backupData.resultNumber.map((n, i) => (
                                        <input
                                            key={i}
                                            readOnly
                                            value={n}
                                            className="result-input placholdercolorchanges2"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <button
                            style={{ marginBottom: 80, marginTop: 20 }}
                            onClick={() => setPopup(true)}
                            className="add-button f3 w-100 fs-xs-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19 white"
                        >Announce Result
                        </button>
                    </div>
                )}
                {popup && (
                    <div className="img-popup-design">
                        <div className="img-popup">
                            <p className="f3 fs-xs-14 fs-sm-15 fs-md-15 fs-lg-15 fs-xl-17 fs-xxl-19 primary">
                                Are you sure you want to Announce Result?
                            </p>
                            <div style={{ paddingTop: 20 }} className="d-flex ac-jb px-2 f3 fs-xs-14 fs-sm-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19">
                                <button onClick={() => { setPopup(false) }} className="no-btn w-40">
                                    No
                                </button>
                                <button
                                    onClick={handleAnnounceResult}
                                    className="yes-btn f2 w-40 f3 fs-xs-14 fs-sm-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19"
                                >
                                    {loading ? <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                            style={{ color: "white" }}
                                        ></span>
                                    </> : 'Yes'}

                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnnounceResultEdit;
